import { take } from "light-observable/observable";
import {
    createDeferredPromise,
    persistedWritable,
    valueWritable,
    type ValueWritable,
} from "ceyper-common";
import { File, type FrameInfo, type LayerInfo } from "$lib/wasm/file";
import {
    getInitialLayerIndex,
    Interaction,
    type SymmetrySettings,
    type ToolOptions,
} from "$lib/wasm/interaction";
import { wasmPromise } from "$lib/wasm/wasm";
import { db } from "$lib/wasm/db";
import { COMMANDS } from "$lib/commands";
import { createEvent } from "$lib/utility/event";
import tools, { type ToolInfo } from "$lib/panels/tools";

console.debug("create state");

const initialize = createDeferredPromise<void>();

export const interaction = valueWritable<Interaction>(null!);
export const frameIndex = valueWritable<number | null>(null);
export const layerIndex = valueWritable<number | null>(null);

type ActiveToolOptions = ValueWritable<ToolOptions | null> & {
    optionChanged: (name: string) => void;
};
export const availableTools = valueWritable([] as (ToolInfo & { index: number })[]);
export const activeTool = valueWritable(-1);
export const activeToolOptions = valueWritable(null) as ActiveToolOptions;
export const symmetrySettings = valueWritable<SymmetrySettings | null>(null);
export const symmetryGuideEditing = valueWritable(false);

export const file = valueWritable<File | null>(null);
export const frames = valueWritable<FrameInfo[] | null>(null);
export const layers = valueWritable<LayerInfo[] | null>(null);

export const initialized = initialize[0];

export const onFilesUpdated = createEvent<void>("files-updated");

const lastActiveFileId = persistedWritable("lastActiveFileId", -1);

// todo move to other file for vite reloading?
wasmPromise.then(init);

async function init() {
    await initInteraction();
    // await initDebugFile();
    await loadLastFile();
    initialize[1]();
}

function autoSave() {
    COMMANDS.invoke("file.save", true);
}

async function initInteraction() {
    const int = new Interaction();
    int.registerCommandHandlers();

    const updateFrames = () => {
        frames.set(file.value!.getFrames()); // todo error handling
    };
    const updateLayers = () => {
        layers.set(file.value!.getLayers(frameIndex.value!)); // todo error handling
    };

    int.frameIndex.subscribe((l) => {
        frameIndex.set(l);
        file.value && updateLayers();
    });
    int.layerIndex.subscribe((l) => layerIndex.set(l));

    // File
    let unsubLayers: { unsubscribe: () => void } | undefined;
    let unsubFrames: { unsubscribe: () => void } | undefined;
    let unsubHistory: { unsubscribe: () => void } | undefined;
    file.subscribe(($file) => {
        unsubLayers?.unsubscribe();
        unsubFrames?.unsubscribe();
        unsubHistory?.unsubscribe();
        if (!$file) {
            layers.set(null);
            frames.set(null);
            int.clearFile();
            return;
        }
        const initialFrame = 0;
        const initialLayer = getInitialLayerIndex($file, initialFrame);

        frameIndex.set(initialFrame);
        layerIndex.set(initialLayer);
        layers.set($file.getLayers(initialFrame));
        frames.set($file.getFrames());
        unsubFrames = $file.onFramesChanged.subscribe(updateFrames);
        unsubLayers = $file.onLayersChanged.subscribe(updateLayers);
        unsubHistory = $file.onHistoryChanged.subscribe(autoSave);

        const lastFile = int.file;
        lastFile?.free();
        int.setFile($file);
    });

    // Tools
    availableTools.set(
        tools
            .map((tool) => ({
                ...tool,
                index: int.tools.indexOf(tool.id),
            }))
            .filter((tool) => tool.index !== -1)
            .sort((a, b) => a.index - b.index) as any
    );

    const onToolChanged = (tool: number) => {
        activeTool.set(tool);
        activeToolOptions.set(int.getToolOptions());
    };
    activeToolOptions.optionChanged = (name: string) => {
        if (!name) return;
        let options = activeToolOptions.value;

        let newValue = options?.get(name)?.value;
        if (newValue === undefined) return;

        int.setToolOptions(options!);
        activeToolOptions.update((v) => v);
    };

    int.onToolChanged.subscribe(onToolChanged);
    onToolChanged(int.tool);

    int.onSymmetryChanged.subscribe((settings) => symmetrySettings.set(settings));
    symmetrySettings.set(int.getSymmetrySettings());

    //
    interaction.set(int);
}

async function loadLastFile() {
    if (lastActiveFileId.value !== -1) {
        const lastFile = await db.getFile(lastActiveFileId.value);
        if (lastFile) {
            file.set(lastFile);
        }
    }

    file.subscribe(($file) => {
        if ($file) {
            if ($file.id) {
                lastActiveFileId.set($file.id);
            } else {
                take(1, $file.onSave).subscribe(() => {
                    if (!$file.id) return;
                    lastActiveFileId.set($file.id);
                    onFilesUpdated.trigger();
                });
            }
        }
    });
}

async function initDebugFile() {
    const newFile = File.new(32, 32);
    // newFile.internal.noise(32, 32);
    file.set(newFile);
}
